import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import Icon from '../../../../../../../atoms/Icon'
import { OUTPUT_TABS } from './testFixtures'

function TestOutput ({ lastResult, activeOutputTab, setActiveOutputTab, onInspectRequest, classes }) {
  const [copied, setCopied] = useState(false)

  const handleCopyResult = useCallback(() => {
    if (lastResult?.result == null) return
    navigator.clipboard.writeText(JSON.stringify(lastResult.result, null, 2)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => { /* clipboard unavailable */ })
  }, [lastResult?.result])

  if (!lastResult) {
    return <div className={classes.noOutput}>No output yet. Run this test to see results.</div>
  }

  return (
    <>
      <div className={classes.outputTabBar}>
        {OUTPUT_TABS.map(tab => (
          <div
            key={tab.id}
            className={`${classes.detailTab} ${activeOutputTab === tab.id ? classes.detailTabActive : ''}`}
            onClick={() => setActiveOutputTab(tab.id)}
          >
            {tab.label}
            {tab.id === 'requests' && lastResult.requests && ` (${lastResult.requests.length})`}
            {tab.id === 'logs' && lastResult.logs && ` (${lastResult.logs.length})`}
          </div>
        ))}
      </div>

      <div className={classes.outputPanel}>
        {lastResult.error && (
          <div className={classes.assertionError}>
            <strong>{lastResult.error.name || 'Error'}:</strong> {lastResult.error.message}
          </div>
        )}
        {lastResult.assertionError && (
          <div className={classes.assertionError}>
            <strong>Assertion failed:</strong> {lastResult.assertionError}
          </div>
        )}

        {activeOutputTab === 'result' && (
          <>
            <div className={`${classes.outputBanner} ${lastResult.pass ? classes.bannerPass : classes.bannerFail}`}>
              <span>{lastResult.pass ? '\u2713 Passed' : '\u2717 Failed'}</span>
              <span style={{ fontWeight: 400 }}>&mdash; {lastResult.executionMs}ms</span>
            </div>
            {lastResult.result != null && (
              <div className={classes.resultWrapper}>
                <pre className={classes.resultPre}>{JSON.stringify(lastResult.result, null, 2)}</pre>
                <button
                  className={classes.resultCopyBtn}
                  onClick={handleCopyResult}
                  title={copied ? 'Copied!' : 'Copy result'}
                  aria-label='Copy result'
                >
                  <Icon name={copied ? 'check' : 'copy'} customSize='13px' />
                </button>
              </div>
            )}
            {lastResult.result == null && !lastResult.error && (
              <div className={classes.noOutput}>No return value.</div>
            )}
          </>
        )}

        {activeOutputTab === 'requests' && (
          <div>
            {(!lastResult.requests || lastResult.requests.length === 0) && (
              <div className={classes.noOutput}>No HTTP requests recorded.</div>
            )}
            {lastResult.requests && lastResult.requests.map((req, i) => (
              <div key={i} className={classes.requestRow} onClick={() => onInspectRequest(req)}>
                <span className={classes.methodBadge}>{req.method}</span>
                <span className={classes.requestUrl}>{req.url}</span>
                <div className={classes.requestMeta}>
                  <span
                    className={classes.statusBadge}
                    style={{ color: req.status < 300 ? 'var(--color-success-600)' : 'var(--color-danger-600)' }}
                  >
                    {req.status}
                  </span>
                  {req.mocked && <span className={classes.mockedTag}>mock</span>}
                  {req.durationMs != null && (
                    <span style={{ fontSize: '10px', color: 'var(--color-neutral-400)' }}>{req.durationMs}ms</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeOutputTab === 'logs' && (
          <div>
            {(!lastResult.logs || lastResult.logs.length === 0) && (
              <div className={classes.noOutput}>No logs recorded.</div>
            )}
            {lastResult.logs && lastResult.logs.map((log, i) => (
              <div key={i} className={classes.logRow}>
                <span
                  className={classes.logLevel}
                  style={{
                    color: log.level === 'error'
                      ? 'var(--color-danger-600)'
                      : log.level === 'warn'
                        ? 'var(--color-warning-600)'
                        : 'var(--color-neutral-400)'
                  }}
                >
                  [{log.level}]
                </span>
                <span className={classes.logMessage}>{log.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

TestOutput.propTypes = {
  lastResult: PropTypes.object,
  activeOutputTab: PropTypes.string.isRequired,
  setActiveOutputTab: PropTypes.func.isRequired,
  onInspectRequest: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

export default TestOutput
